import { useCallback, useEffect, useRef, useState } from "react";
import { useSettings } from "@components/settings/SettingsProvider";

export interface ScheduleSettingsDetour {
    /** True while the dialog is set aside for the settings it opened. */
    isAway: boolean;
    leaveFor: (path: string[]) => void;
}

/**
 * Lets a schedule dialog step aside for settings (e.g. to unlock a period)
 * and come back when they close, instead of the user starting over.
 * `onReturn` runs once on the way back, so the caller can refresh what the
 * settings change may have affected.
 */
export function useScheduleSettingsDetour(
    onReturn: () => void,
): ScheduleSettingsDetour {
    const { open, isOpened } = useSettings();
    const [isAway, setIsAway] = useState(false);
    const hasSettingsOpenedRef = useRef(false);
    const onReturnRef = useRef(onReturn);
    useEffect(() => {
        onReturnRef.current = onReturn;
    }, [onReturn]);

    useEffect(() => {
        if (!isAway) {
            return;
        }
        if (isOpened) {
            hasSettingsOpenedRef.current = true;
            return;
        }
        if (hasSettingsOpenedRef.current) {
            hasSettingsOpenedRef.current = false;
            setIsAway(false);
            onReturnRef.current();
        }
    }, [isAway, isOpened]);

    const leaveFor = useCallback(
        (path: string[]) => {
            setIsAway(true);
            open(path);
        },
        [open],
    );

    return { isAway, leaveFor };
}
